import { useEffect, useRef } from 'react';

import type { DealItemsActionType } from '@/lib/deal-events.types';
import { getDealEventsUrl } from '@/lib/config';
import { isDealEventsSseEnabled } from '@/lib/deal-sse-flag';
import { createDealSseAnalytics } from '@/lib/sse-analytics';
import { readSseFrames } from '@/lib/streaming/sse';
import { parseDealEvent } from '@/lib/utils/deal-event-stream';
import { useAuth0 } from '@auth0/auth0-react';

type DealEventStreamHandlers = {
  /**
   * A deal item changed — reload the deal items list. `action` carries the
   * backend action type for `ITEMS` signals; it is `undefined` for the `SYNC`
   * reload-everything signal.
   */
  onItems?: (action?: DealItemsActionType) => void;
  /** The deal itself changed — re-query `getDeal`. Also fires on `SYNC`. */
  onDeal?: () => void;
  /**
   * Extra local gate on top of the global feature flag. Defaults to `true`.
   * Lets a caller keep the stream closed while some precondition isn't met.
   */
  enabled?: boolean;
};

// Reconnect backoff. The backend gates the endpoint on auth + deal access, so a
// dropped connection is usually transient (idle proxy, redeploy) — retry with a
// capped exponential backoff.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

function backoffDelay(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

/**
 * Subscribe to a deal's SSE "signal" stream (`GET /deals/:dealId/events`) and
 * turn each `{ kind }` notification into a GraphQL refetch.
 *
 * This is the client half of the migration away from the `dealUpdated` /
 * `dealItemsUpdatedV3` WebSocket subscriptions: instead of pushing full deal /
 * item payloads, the server pushes a lightweight signal and the client pulls
 * fresh data through the queries it already owns.
 *
 * The stream is read over `fetch` (not `EventSource`) because the backend
 * authenticates via the `Authorization` header, which `EventSource` cannot set.
 * The token is sent as `Bearer <token>`, matching the deal-items SSE stream
 * (`stream-deal-items.ts`) — same backend, same `$auth` plugin.
 *
 * No-op unless `isDealEventsSseEnabled()`, so it can live alongside the existing
 * subscriptions until the approach is validated.
 */
export function useDealEventStream(
  dealId: string | undefined,
  { onItems, onDeal, enabled = true }: DealEventStreamHandlers
): void {
  const { getAccessTokenSilently } = useAuth0();

  // Keep the latest handlers in refs so changing their identity (a common case
  // with inline arrow functions) does not tear down and reopen the stream.
  const onItemsRef = useRef(onItems);
  const onDealRef = useRef(onDeal);
  onItemsRef.current = onItems;
  onDealRef.current = onDeal;

  const active = enabled && isDealEventsSseEnabled() && Boolean(dealId);

  useEffect(() => {
    if (!active || !dealId) return;

    const abort = new AbortController();
    const { signal } = abort;
    const analytics = createDealSseAnalytics(dealId);

    const dispatch = (frameData: string): void => {
      const parsed = parseDealEvent(frameData);
      if (!parsed) return;
      switch (parsed.kind) {
        case 'SYNC':
          // (Re)connect catch-up — reload everything.
          onDealRef.current?.();
          onItemsRef.current?.();
          break;
        case 'DEAL':
          onDealRef.current?.();
          break;
        case 'ITEMS':
          onItemsRef.current?.(parsed.action);
          break;
      }
    };

    const run = async (): Promise<void> => {
      let attempt = 0;
      while (!signal.aborted) {
        let connected = false;
        try {
          const token = await getAccessTokenSilently();
          if (signal.aborted) return;

          const response = await fetch(getDealEventsUrl(dealId), {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
            },
            signal,
          });
          if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed (${response.status})`);
          }

          attempt = 0; // reset backoff after a successful connect
          connected = true;
          analytics.onConnected();
          await readSseFrames(response.body, (frame) => dispatch(frame.data));
          // read() completed without error → the server closed the stream.
          analytics.onDropped('ended');
        } catch {
          // AbortError (unmount / dealId change) exits via the loop guard; a
          // mid-stream failure is a drop; a failure before `connected` is a
          // connect attempt that just retries below.
          if (signal.aborted) return;
          if (connected) analytics.onDropped('error');
        }

        if (signal.aborted) return;
        attempt += 1;
        await sleep(backoffDelay(attempt), signal);
      }
    };

    void run();

    return () => abort.abort();
  }, [active, dealId, getAccessTokenSilently]);
}
